<template>
  <aside class="flow-inspector">
    <div class="flow-inspector-header">
      <div>
        <span class="flow-inspector-eyebrow">{{ eyebrow }}</span>
        <h2>{{ title }}</h2>
      </div>
      <button v-if="selection !== null" type="button" class="btn btn-sm btn-ghost btn-circle" title="Close editor" @click="emit('close')">
        <Icon icon="tabler:x" width="17" height="17" />
      </button>
    </div>

    <div v-if="selection === null" class="flow-inspector-empty">
      <span class="flow-inspector-empty-icon">
        <Icon icon="tabler:hand-click" width="27" height="27" />
      </span>
      <h3>Select a step</h3>
      <p>Choose any step or connection on the canvas. Its simple settings will appear here.</p>
      <div class="flow-inspector-guide">
        <span><b>1</b> Add a step from the left</span>
        <span><b>2</b> Connect it to the previous step</span>
        <span><b>3</b> Fill in the settings here</span>
      </div>
    </div>

    <template v-else-if="selection.kind === 'edge'">
      <div class="flow-inspector-body">
        <div class="flow-friendly-card">
          <Icon icon="tabler:route" width="20" height="20" />
          <p>This connection decides when the conversation should move to the next step.</p>
        </div>
        <label class="flow-field">
          <span>When should this path be used?</span>
          <textarea v-model="edgeDraft.condition" class="textarea textarea-bordered" rows="5" placeholder="Example: Use this path when the customer says yes"></textarea>
          <small>Leave this empty when this is the normal next step. Use <code v-pre>{{customer}}</code> to insert a value or JSON object at runtime.</small>
        </label>
      </div>
      <div class="flow-inspector-footer">
        <button type="button" class="btn btn-error btn-sm" @click="emit('delete-selection')">Delete connection</button>
        <button type="button" class="btn btn-sm" @click="saveEdge">Save</button>
      </div>
    </template>

    <template v-else>
      <div class="flow-inspector-tabs" role="tablist" aria-label="Step settings">
        <button type="button" :class="{ active: activeTab === 'basics' }" @click="activeTab = 'basics'">Basics</button>
        <button v-if="showSetupTab === true" type="button" :class="{ active: activeTab === 'setup' }" @click="activeTab = 'setup'">Setup</button>
        <button v-if="showDataTab === true" type="button" :class="{ active: activeTab === 'data' }" @click="activeTab = 'data'">Data</button>
        <button v-if="showButtonsTab === true" type="button" :class="{ active: activeTab === 'buttons' }" @click="activeTab = 'buttons'">Buttons</button>
      </div>

      <div class="flow-inspector-body">
        <section v-show="activeTab === 'basics'">
          <label class="flow-field">
            <span>Step name</span>
            <input v-model="nodeDraft.label" class="input input-bordered" type="text" placeholder="Give this step a clear name" />
          </label>
          <label v-if="showMessage === true" class="flow-field">
            <span>{{ messageLabel }}</span>
            <textarea v-model="nodeDraft.message" class="textarea textarea-bordered" rows="4" :placeholder="messagePlaceholder"></textarea>
          </label>
          <label v-if="showMessage === true" class="flow-check-card">
            <input v-model="nodeDraft.useExactMessage" type="checkbox" class="checkbox" />
            <span>
              <b>Say this exact message</b>
              <small>Turn this on when the wording must not change.</small>
            </span>
          </label>
          <label class="flow-field">
            <span>What should happen in this step?</span>
            <textarea v-model="nodeDraft.stepDescription" class="textarea textarea-bordered" rows="4" placeholder="Describe the goal in plain language"></textarea>
            <small>Use <code v-pre>{{customer}}</code> for a value. Objects are inserted as JSON text, including nested values.</small>
          </label>
        </section>

        <section v-show="activeTab === 'setup' && showSetupTab === true">
          <template v-if="nodeDraft.nodeType === 'If'">
            <div class="flow-friendly-card">
              <Icon icon="tabler:git-branch" width="20" height="20" />
              <p>Choose information from an earlier step, then tell the agent what value to compare.</p>
            </div>
            <label class="flow-field">
              <span>Information to check</span>
              <input v-model="nodeDraft.config.sourceKey" class="input input-bordered" type="text" placeholder="Example: customerType" />
              <small>Use an object path such as customer.address.city.</small>
            </label>
            <label class="flow-field">
              <span>Comparison</span>
              <select v-model="nodeDraft.config.operator" class="select select-bordered">
                <option v-for="operator in operators" :key="operator.value" :value="operator.value">{{ operator.label }}</option>
              </select>
            </label>
            <label v-if="operatorNeedsValue === true" class="flow-field">
              <span>Value to compare with</span>
              <input v-model="nodeDraft.config.right" class="input input-bordered" type="text" placeholder="Example: premium" />
            </label>
          </template>

          <template v-if="nodeDraft.nodeType === 'Action'">
            <label v-if="isJavaScriptNode === false" class="flow-field">
              <span>Action</span>
              <select v-model="nodeDraft.actionType" class="select select-bordered" @change="changeActionType">
                <option value="activateTool">Run a tool</option>
                <option value="httpRequest">Call an API</option>
                <option value="announce">Play an announcement</option>
                <option value="transferCall">Transfer the call</option>
                <option value="endCall">End the call</option>
              </select>
            </label>

            <template v-if="nodeDraft.actionType === 'activateTool'">
              <label v-if="isJavaScriptNode === false" class="flow-field">
                <span>Tool source</span>
                <select v-model="nodeDraft.toolSource" class="select select-bordered">
                  <option value="storedTool">Connected tool</option>
                  <option value="inlineJsTool">JavaScript snippet</option>
                </select>
              </label>
              <template v-if="nodeDraft.toolSource === 'storedTool'">
                <label class="flow-field">
                  <span>Tool to run</span>
                  <select v-model="nodeDraft.selectedToolId" class="select select-bordered">
                    <option :value="null">Choose a tool</option>
                    <option v-for="tool in normalizedTools" :key="tool.id" :value="tool.id">{{ tool.name }}</option>
                  </select>
                </label>
                <div v-if="selectedToolParams.length > 0" class="flow-subsection">
                  <h3>Values sent to the tool</h3>
                  <label v-for="param in selectedToolParams" :key="param.name" class="flow-field">
                    <span>{{ param.name }} <b v-if="param.required === true">*</b></span>
                    <input v-model="nodeDraft.toolInputMappings[param.name]" class="input input-bordered" type="text" :placeholder="`Text or {{variableName}}`" />
                  </label>
                </div>
              </template>
              <template v-else>
                <div v-if="isJavaScriptNode === true" class="flow-friendly-card">
                  <Icon icon="tabler:code" width="20" height="20" />
                  <p>Use variables and context in your code. Return an object to save values, including complete nested objects. Use await httpRequest({ url, method, headers, body }) for an approved API call.</p>
                </div>
                <label class="flow-field">
                  <span>Snippet name</span>
                  <input v-model="nodeDraft.inlineTool.toolName" class="input input-bordered" type="text" placeholder="Example: calculatePrice" />
                </label>
                <label v-if="isJavaScriptNode === false" class="flow-field">
                  <span>Description</span>
                  <textarea v-model="nodeDraft.inlineTool.description" class="textarea textarea-bordered" rows="2"></textarea>
                </label>
                <label v-if="isJavaScriptNode === false" class="flow-field">
                  <span>Required parameters</span>
                  <input v-model="requiredParamsText" class="input input-bordered" type="text" placeholder="customerId, amount" />
                  <small>Separate names with commas.</small>
                </label>
                <label class="flow-field">
                  <span>Optional parameters</span>
                  <input v-model="optionalParamsText" class="input input-bordered" type="text" placeholder="currency, note" />
                </label>
                <label class="flow-field">
                  <span>JavaScript</span>
                  <textarea v-model="nodeDraft.inlineTool.functionConfig" class="textarea textarea-bordered flow-code" rows="12" dir="ltr"></textarea>
                  <small v-if="isJavaScriptNode === true">Example result: return { customer: { name: variables.customerName, tier: 'gold' } };</small>
                </label>
              </template>
            </template>

            <template v-if="nodeDraft.actionType === 'httpRequest'">
              <div class="flow-inline-fields">
                <label class="flow-field flow-method-field">
                  <span>Method</span>
                  <select v-model="nodeDraft.config.method" class="select select-bordered">
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                  </select>
                </label>
                <label class="flow-field">
                  <span>API address</span>
                  <input v-model="nodeDraft.config.url" class="input input-bordered" type="text" dir="ltr" placeholder="https://api.example.com" />
                </label>
              </div>
              <FlowManagerKeyValueEditor title="Headers" key-label="Header" value-label="Value" :items="nodeDraft.config.headersList" @update="nodeDraft.config.headersList = $event" />
              <FlowManagerKeyValueEditor title="Request body fields" key-label="Field" value-label="Value" :items="nodeDraft.config.requestFields" @update="nodeDraft.config.requestFields = $event" />
              <label class="flow-field">
                <span>Raw JSON body</span>
                <textarea v-model="nodeDraft.config.body" class="textarea textarea-bordered flow-code" rows="7" dir="ltr"></textarea>
              </label>
              <label class="flow-field">
                <span>Save the full response as</span>
                <input v-model="nodeDraft.config.outputVariable" class="input input-bordered" type="text" placeholder="Example: apiResult" />
              </label>
              <FlowManagerKeyValueEditor title="Response values to save" key-label="Response path" value-label="Variable name" :items="responseMappingItems" @update="updateResponseMappings" />
            </template>

            <template v-if="nodeDraft.actionType === 'announce'">
              <label class="flow-check-card">
                <input v-model="nodeDraft.config.allowUserInterrupt" type="checkbox" class="checkbox" />
                <span>
                  <b>Let the caller interrupt</b>
                  <small>The caller can speak before the announcement finishes.</small>
                </span>
              </label>
            </template>

            <template v-if="nodeDraft.actionType === 'transferCall'">
              <label class="flow-field">
                <span>Transfer type</span>
                <select v-model="nodeDraft.config.transferType" class="select select-bordered" :disabled="nodeDraft.config.lockTransferType === true">
                  <option value="phoneNumber">Phone number</option>
                  <option value="sipTransfer">SIP destination</option>
                </select>
              </label>
              <label class="flow-field">
                <span>Phone number</span>
                <input v-model="nodeDraft.config.phoneNumber" class="input input-bordered" type="text" placeholder="0501234567 or {{phone}}" />
              </label>
              <div v-if="nodeDraft.config.transferType === 'sipTransfer'" class="flow-inline-fields">
                <label class="flow-field">
                  <span>SIP IP</span>
                  <input v-model="nodeDraft.config.sipIp" class="input input-bordered" type="text" dir="ltr" placeholder="127.0.0.1" />
                </label>
                <label class="flow-field flow-port-field">
                  <span>Port</span>
                  <input v-model="nodeDraft.config.sipPort" class="input input-bordered" type="number" min="1" max="65535" placeholder="5060" />
                </label>
              </div>
              <div class="flow-result-card">
                <span>Final destination</span>
                <code>{{ transferDestination.length > 0 ? transferDestination : 'Complete the fields above' }}</code>
              </div>
              <label class="flow-field">
                <span>Delay before transfer in milliseconds</span>
                <input v-model.number="nodeDraft.config.delayMs" class="input input-bordered" type="number" min="0" />
              </label>
            </template>

            <template v-if="nodeDraft.actionType === 'endCall'">
              <div class="flow-friendly-card">
                <Icon icon="tabler:phone-off" width="20" height="20" />
                <p>The call ends after the message on the Basics tab has been spoken.</p>
              </div>
              <label class="flow-field">
                <span>Delay before ending in milliseconds</span>
                <input v-model.number="nodeDraft.config.delayMs" class="input input-bordered" type="number" min="0" />
              </label>
            </template>
          </template>
        </section>

        <section v-show="activeTab === 'data' && showDataTab === true">
          <div class="flow-section-heading">
            <div>
              <h3>Information to collect</h3>
              <p>The agent waits for required information before moving on.</p>
            </div>
            <button type="button" class="btn btn-sm" @click="addVariable">Add field</button>
          </div>
          <div v-if="nodeDraft.variablesToCollect.length === 0" class="flow-mini-empty">No information is collected in this step.</div>
          <article v-for="(variable, index) in nodeDraft.variablesToCollect" :key="`variable-${index}`" class="flow-repeat-card">
            <div class="flow-repeat-head">
              <strong>Field {{ index + 1 }}</strong>
              <button type="button" class="btn btn-error btn-sm" @click="nodeDraft.variablesToCollect.splice(index, 1)">Remove</button>
            </div>
            <label class="flow-field">
              <span>Name</span>
              <input v-model="variable.name" class="input input-bordered" type="text" placeholder="Example: customerName" />
            </label>
            <div class="flow-inline-fields">
              <label class="flow-field">
                <span>Type</span>
                <select v-model="variable.type" class="select select-bordered">
                  <option value="string">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Yes or no</option>
                  <option value="date">Date</option>
                  <option value="object">Object</option>
                </select>
              </label>
              <label class="flow-field">
                <span>Validation</span>
                <select v-model="variable.validationPattern" class="select select-bordered">
                  <option v-for="validation in validations" :key="validation.value" :value="validation.value">{{ validation.label }}</option>
                </select>
              </label>
            </div>
            <label class="flow-field">
              <span>Description</span>
              <input v-model="variable.description" class="input input-bordered" type="text" placeholder="Explain what to collect" />
            </label>
            <label v-if="variable.validationPattern === 'custom'" class="flow-field">
              <span>Custom regular expression</span>
              <input v-model="variable.customRegex" class="input input-bordered" type="text" dir="ltr" />
            </label>
            <label class="flow-check-card flow-check-card-compact">
              <input v-model="variable.required" type="checkbox" class="checkbox checkbox-sm" />
              <span><b>Required before continuing</b></span>
            </label>
          </article>
          <div v-if="showContextTools === true" class="flow-subsection">
            <h3>Tools available while talking</h3>
            <label v-for="tool in normalizedTools" :key="`context-tool-${tool.id}`" class="flow-check-card flow-check-card-compact">
              <input type="checkbox" class="checkbox checkbox-sm" :checked="nodeDraft.toolsToUse.includes(tool.id) === true" @change="toggleContextTool(tool.id)" />
              <span><b>{{ tool.name }}</b></span>
            </label>
          </div>
        </section>

        <section v-show="activeTab === 'buttons' && showButtonsTab === true">
          <div class="flow-section-heading">
            <div>
              <h3>Quick reply buttons</h3>
              <p>Give users simple choices during this step.</p>
            </div>
            <button type="button" class="btn btn-sm" @click="addButton">Add button</button>
          </div>
          <div v-if="buttonError !== ''" role="alert" class="alert alert-error flow-inline-alert">{{ buttonError }}</div>
          <div v-if="nodeDraft.buttons.length === 0" class="flow-mini-empty">No buttons are shown in this step.</div>
          <article v-for="(button, index) in nodeDraft.buttons" :key="button.id" class="flow-repeat-card">
            <div class="flow-repeat-head">
              <strong>Button {{ index + 1 }}</strong>
              <button type="button" class="btn btn-error btn-sm" @click="removeButton(index)">Remove</button>
            </div>
            <label class="flow-field">
              <span>Text shown to the user</span>
              <input v-model="button.label" class="input input-bordered" type="text" placeholder="Example: Yes, continue" />
            </label>
            <label class="flow-field">
              <span>What happens when clicked?</span>
              <select v-model="button.type" class="select select-bordered" @change="button.action = ''">
                <option value="Text">Send this text</option>
                <option value="Link">Open a link</option>
                <option value="JumpToStep">Go to another step</option>
              </select>
            </label>
            <label v-if="button.type === 'Link'" class="flow-field">
              <span>Link</span>
              <input v-model="button.action" class="input input-bordered" type="url" dir="ltr" placeholder="https://example.com" />
            </label>
            <label v-if="button.type === 'JumpToStep'" class="flow-field">
              <span>Destination step</span>
              <select v-model="button.action" class="select select-bordered">
                <option value="">Choose a step</option>
                <option v-for="node in jumpTargets" :key="node.id" :value="node.id">{{ node.data.label }}</option>
              </select>
            </label>
          </article>
        </section>
      </div>

      <div class="flow-inspector-footer">
        <button v-if="canDeleteNode === true" type="button" class="btn btn-error btn-sm" @click="emit('delete-selection')">Delete step</button>
        <span v-else></span>
        <button type="button" class="btn btn-sm" @click="saveNode">Save changes</button>
      </div>
    </template>
  </aside>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import FlowManagerKeyValueEditor from './FlowManagerKeyValueEditor.vue';
import { deepCopy, hasText } from './flowManagerModel';

const props = defineProps({
  selection: {
    type: Object,
    default: null
  },
  tools: {
    type: Array,
    default: () => []
  },
  nodes: {
    type: Array,
    default: () => []
  },
  selectedChannels: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['close', 'save-node', 'save-edge', 'delete-selection']);
const activeTab = ref('basics');
const nodeDraft = ref({});
const edgeDraft = ref({ condition: '' });
const requiredParamsText = ref('');
const optionalParamsText = ref('');
const buttonError = ref('');

const operators = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'greaterThan', label: 'Is greater than' },
  { value: 'greaterThanOrEqual', label: 'Is at least' },
  { value: 'lessThan', label: 'Is less than' },
  { value: 'lessThanOrEqual', label: 'Is at most' },
  { value: 'exists', label: 'Exists' },
  { value: 'notExists', label: 'Does not exist' },
  { value: 'isTrue', label: 'Is yes' },
  { value: 'isFalse', label: 'Is no' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' }
];

const validations = [
  { value: '', label: 'No special format' },
  { value: 'digits', label: 'Digits only' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email address' },
  { value: 'phone', label: 'Phone number' },
  { value: 'israeliId', label: 'Israeli ID' },
  { value: 'dateIso', label: 'Date YYYY-MM-DD' },
  { value: 'time24h', label: 'Time HH:mm' },
  { value: 'uuid', label: 'UUID' },
  { value: 'custom', label: 'Custom format' }
];

const title = computed(() => {
  if (props.selection === null) {
    return 'Step settings';
  }
  if (props.selection.kind === 'edge') {
    return 'Connection';
  }
  return hasText(nodeDraft.value.label) === true ? nodeDraft.value.label : 'Untitled step';
});

const eyebrow = computed(() => {
  if (props.selection === null) {
    return 'Inspector';
  }
  return props.selection.kind === 'edge' ? 'Path settings' : 'Step settings';
});

const showSetupTab = computed(() => ['If', 'Action'].includes(nodeDraft.value.nodeType) === true);
const showDataTab = computed(() => ['Question', 'Root', 'KnowledgeBase', 'Action'].includes(nodeDraft.value.nodeType) === true);
const isJavaScriptNode = computed(() => nodeDraft.value.config !== null && nodeDraft.value.config !== undefined && nodeDraft.value.config.isJavaScriptNode === true);
const showButtonsTab = computed(() => {
  if (['Question', 'Root', 'KnowledgeBase', 'Action'].includes(nodeDraft.value.nodeType) === false) {
    return false;
  }
  return props.selectedChannels.includes('Web') === true || props.selectedChannels.includes('Whatsapp') === true;
});
const showContextTools = computed(() => ['Question', 'Root'].includes(nodeDraft.value.nodeType) === true);
const showMessage = computed(() => ['Question', 'Root', 'Action'].includes(nodeDraft.value.nodeType) === true);
const canDeleteNode = computed(() => nodeDraft.value.nodeType !== 'Root');

const messageLabel = computed(() => {
  if (nodeDraft.value.actionType === 'endCall') {
    return 'Message before ending the call';
  }
  if (nodeDraft.value.actionType === 'announce') {
    return 'Announcement';
  }
  return 'Message to the user';
});

const messagePlaceholder = computed(() => {
  if (nodeDraft.value.actionType === 'endCall') {
    return 'What should the agent say before hanging up?';
  }
  return 'What should the agent say at this step?';
});

const operatorNeedsValue = computed(() => {
  const value = nodeDraft.value.config !== undefined ? nodeDraft.value.config.operator : '';
  return ['exists', 'notExists', 'isTrue', 'isFalse', 'isEmpty', 'isNotEmpty'].includes(value) === false;
});

const normalizedTools = computed(() => {
  const output = [];
  for (let index = 0; index < props.tools.length; index = index + 1) {
    const tool = props.tools[index];
    const rawId = tool.toolId !== undefined ? tool.toolId : tool.toolid;
    const id = Number.parseInt(String(rawId), 10);
    if (Number.isFinite(id) === false) {
      continue;
    }
    const rawName = tool.toolName !== undefined ? tool.toolName : tool.toolname;
    const name = hasText(rawName) === true ? rawName : `Tool ${id}`;
    const data = tool.toolData !== undefined ? tool.toolData : tool.tooldata;
    output.push({ id, name, data: data !== null && data !== undefined && typeof data === 'object' ? data : {} });
  }
  return output;
});

const selectedToolParams = computed(() => {
  const selected = normalizedTools.value.find(tool => tool.id === nodeDraft.value.selectedToolId);
  if (selected === undefined) {
    return [];
  }
  const params = [];
  const required = Array.isArray(selected.data.requiredParams) === true ? selected.data.requiredParams : [];
  const optional = Array.isArray(selected.data.optionalParams) === true ? selected.data.optionalParams : [];
  for (let index = 0; index < required.length; index = index + 1) {
    params.push({ name: required[index], required: true });
  }
  for (let index = 0; index < optional.length; index = index + 1) {
    params.push({ name: optional[index], required: false });
  }
  return params;
});

const jumpTargets = computed(() => props.nodes.filter(node => node.id !== props.selection.id));

const transferDestination = computed(() => {
  if (nodeDraft.value.config === undefined) {
    return '';
  }
  const phone = hasText(nodeDraft.value.config.phoneNumber) === true ? nodeDraft.value.config.phoneNumber.trim() : '';
  if (nodeDraft.value.config.transferType !== 'sipTransfer') {
    return phone;
  }
  const ip = hasText(nodeDraft.value.config.sipIp) === true ? nodeDraft.value.config.sipIp.trim() : '';
  const port = String(nodeDraft.value.config.sipPort !== undefined ? nodeDraft.value.config.sipPort : '').trim();
  if (phone.length === 0) {
    return '';
  }
  if (ip.length === 0) {
    return '';
  }
  if (port.length === 0) {
    return '';
  }
  return `sip:${phone}@${ip}:${port}`;
});

const responseMappingItems = computed(() => {
  const mappings = nodeDraft.value.config !== undefined && Array.isArray(nodeDraft.value.config.responseMappings) === true ? nodeDraft.value.config.responseMappings : [];
  return mappings.map(mapping => ({ key: mapping.path, value: mapping.variable }));
});

watch(() => props.selection, selection => {
  activeTab.value = 'basics';
  buttonError.value = '';
  if (selection === null) {
    nodeDraft.value = {};
    edgeDraft.value = { condition: '' };
    return;
  }
  if (selection.kind === 'edge') {
    edgeDraft.value = {
      condition: selection.element.data !== null && selection.element.data !== undefined && hasText(selection.element.data.ifCondition) === true
        ? selection.element.data.ifCondition
        : (hasText(selection.element.label) === true ? selection.element.label : '')
    };
    return;
  }
  nodeDraft.value = normalizeNodeDraft(selection.element.data);
  requiredParamsText.value = nodeDraft.value.inlineTool.requiredParams.join(', ');
  optionalParamsText.value = nodeDraft.value.inlineTool.optionalParams.join(', ');
}, { immediate: true, deep: true });

watch(showButtonsTab, isVisible => {
  if (isVisible === false && activeTab.value === 'buttons') {
    activeTab.value = 'basics';
  }
});

function normalizeNodeDraft(data) {
  const draft = deepCopy(data);
  draft.label = hasText(draft.label) === true ? draft.label : 'Untitled step';
  draft.stepDescription = hasText(draft.stepDescription) === true ? draft.stepDescription : '';
  draft.message = hasText(draft.message) === true ? draft.message : '';
  draft.useExactMessage = draft.useExactMessage === true;
  draft.variablesToCollect = Array.isArray(draft.variablesToCollect) === true ? draft.variablesToCollect : [];
  draft.toolsToUse = Array.isArray(draft.toolsToUse) === true ? draft.toolsToUse : [];
  draft.buttons = Array.isArray(draft.buttons) === true ? draft.buttons : [];
  draft.config = draft.config !== null && draft.config !== undefined && typeof draft.config === 'object' ? draft.config : {};
  draft.toolInputMappings = draft.toolInputMappings !== null && draft.toolInputMappings !== undefined && typeof draft.toolInputMappings === 'object' ? draft.toolInputMappings : {};
  draft.inlineTool = draft.inlineTool !== null && draft.inlineTool !== undefined && typeof draft.inlineTool === 'object' ? draft.inlineTool : {};
  draft.inlineTool.toolName = hasText(draft.inlineTool.toolName) === true ? draft.inlineTool.toolName : '';
  draft.inlineTool.description = hasText(draft.inlineTool.description) === true ? draft.inlineTool.description : '';
  draft.inlineTool.functionConfig = hasText(draft.inlineTool.functionConfig) === true ? draft.inlineTool.functionConfig : '';
  draft.inlineTool.requiredParams = Array.isArray(draft.inlineTool.requiredParams) === true ? draft.inlineTool.requiredParams : [];
  draft.inlineTool.optionalParams = Array.isArray(draft.inlineTool.optionalParams) === true ? draft.inlineTool.optionalParams : [];
  normalizeActionConfig(draft);
  return draft;
}

function normalizeActionConfig(draft) {
  if (draft.nodeType !== 'Action') {
    return;
  }
  draft.actionType = hasText(draft.actionType) === true ? draft.actionType : 'activateTool';
  draft.toolSource = hasText(draft.toolSource) === true ? draft.toolSource : 'storedTool';
  if (draft.actionType === 'httpRequest') {
    draft.config.method = hasText(draft.config.method) === true ? draft.config.method : 'POST';
    draft.config.url = hasText(draft.config.url) === true ? draft.config.url : '';
    draft.config.headersList = Array.isArray(draft.config.headersList) === true ? draft.config.headersList : [];
    draft.config.requestFields = Array.isArray(draft.config.requestFields) === true ? draft.config.requestFields : [];
    draft.config.body = hasText(draft.config.body) === true ? draft.config.body : '{}';
    draft.config.outputVariable = hasText(draft.config.outputVariable) === true ? draft.config.outputVariable : '';
    draft.config.responseMappings = Array.isArray(draft.config.responseMappings) === true ? draft.config.responseMappings : [];
  }
  if (draft.actionType === 'announce' && draft.config.allowUserInterrupt !== false) {
    draft.config.allowUserInterrupt = true;
  }
  if (draft.actionType === 'transferCall') {
    const destination = hasText(draft.config.destination) === true ? draft.config.destination.trim() : '';
    const sipMatch = destination.match(/^sip:([^@]+)@([^:]+):(\d+)$/i);
    const destinationLooksSip = [destination.startsWith('sip:') === true, destination.includes('@') === true].includes(true);
    draft.config.transferType = [draft.config.transferType === 'sipTransfer', destinationLooksSip === true].includes(true) ? 'sipTransfer' : 'phoneNumber';
    draft.config.phoneNumber = hasText(draft.config.phoneNumber) === true ? draft.config.phoneNumber : (sipMatch !== null ? sipMatch[1] : destination);
    draft.config.sipIp = hasText(draft.config.sipIp) === true ? draft.config.sipIp : (sipMatch !== null ? sipMatch[2] : '');
    draft.config.sipPort = draft.config.sipPort !== undefined ? draft.config.sipPort : (sipMatch !== null ? sipMatch[3] : '');
  }
  if (['transferCall', 'endCall'].includes(draft.actionType) === true) {
    draft.config.delayMode = 'afterAssistantReply';
    const delay = Number(draft.config.delayMs);
    draft.config.delayMs = Number.isFinite(delay) === true && delay >= 0 ? delay : 0;
  }
}

function changeActionType() {
  normalizeActionConfig(nodeDraft.value);
  if (nodeDraft.value.actionType === 'announce') {
    nodeDraft.value.useExactMessage = true;
  }
}

function addVariable() {
  nodeDraft.value.variablesToCollect.push({
    name: '',
    type: 'string',
    description: '',
    required: false,
    validationPattern: '',
    customRegex: ''
  });
}

function toggleContextTool(toolId) {
  const index = nodeDraft.value.toolsToUse.indexOf(toolId);
  if (index >= 0) {
    nodeDraft.value.toolsToUse.splice(index, 1);
    return;
  }
  nodeDraft.value.toolsToUse.push(toolId);
}

function createLocalId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `button-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function addButton() {
  buttonError.value = '';
  nodeDraft.value.buttons.push({ id: createLocalId(), label: '', type: 'Text', action: '' });
}

function removeButton(index) {
  buttonError.value = '';
  nodeDraft.value.buttons.splice(index, 1);
}

function updateResponseMappings(items) {
  nodeDraft.value.config.responseMappings = items.map(item => ({ path: item.key, variable: item.value }));
}

function normalizeCommaList(value) {
  const parts = value.split(',');
  const output = [];
  for (let index = 0; index < parts.length; index = index + 1) {
    const item = parts[index].trim();
    if (item.length > 0 && output.includes(item) === false) {
      output.push(item);
    }
  }
  return output;
}

function saveNode() {
  buttonError.value = '';
  const buttons = nodeDraft.value.buttons;
  for (let index = 0; index < buttons.length; index = index + 1) {
    const button = buttons[index];
    if (hasText(button.label) === false) {
      activeTab.value = 'buttons';
      buttonError.value = `Button ${index + 1} needs text.`;
      return;
    }
    if (button.type === 'Link' && hasText(button.action) === false) {
      activeTab.value = 'buttons';
      buttonError.value = `Button ${index + 1} needs a link.`;
      return;
    }
    if (button.type === 'JumpToStep' && hasText(button.action) === false) {
      activeTab.value = 'buttons';
      buttonError.value = `Button ${index + 1} needs a destination step.`;
      return;
    }
  }
  nodeDraft.value.inlineTool.requiredParams = normalizeCommaList(requiredParamsText.value);
  nodeDraft.value.inlineTool.optionalParams = normalizeCommaList(optionalParamsText.value);
  nodeDraft.value.variablesToCollect = nodeDraft.value.variablesToCollect.filter(variable => hasText(variable.name) === true);
  if (nodeDraft.value.actionType === 'transferCall') {
    nodeDraft.value.config.destination = transferDestination.value;
  }
  if (nodeDraft.value.actionType === 'announce') {
    nodeDraft.value.useExactMessage = true;
  }
  emit('save-node', deepCopy(nodeDraft.value));
}

function saveEdge() {
  emit('save-edge', edgeDraft.value.condition);
}
</script>

<style scoped>
.flow-inspector {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  border-left: 1px solid #d8e1ed;
  background: #f8fafc;
}

.flow-inspector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1rem 0.8rem;
  border-bottom: 1px solid #dfe7f0;
}

.flow-inspector-eyebrow {
  color: #2f72c7;
  font-size: 0.61rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.flow-inspector-header h2 {
  margin: 0.24rem 0 0;
  color: #10233e;
  font-size: 0.95rem;
  font-weight: 720;
}

.flow-inspector-empty {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.5rem;
  text-align: center;
}

.flow-inspector-empty-icon {
  width: 3.2rem;
  height: 3.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d4e3f5;
  border-radius: 0.65rem;
  background: #edf4fb;
  color: #2f72c7;
}

.flow-inspector-empty h3 {
  margin: 1rem 0 0;
  font-size: 1rem;
  color: #10233e;
  font-weight: 720;
}

.flow-inspector-empty p {
  margin: 0.4rem 0 0;
  max-width: 17rem;
  color: #62748b;
  font-size: 0.74rem;
  line-height: 1.55;
}

.flow-inspector-guide {
  width: 100%;
  margin-top: 1.3rem;
  padding: 0.75rem;
  border: 1px solid #dce6f1;
  border-radius: 0.55rem;
  background: #f1f6fb;
  text-align: left;
}

.flow-inspector-guide span {
  display: flex;
  align-items: center;
  color: #4d6480;
  font-size: 0.68rem;
}

.flow-inspector-guide span + span {
  margin-top: 0.55rem;
}

.flow-inspector-guide b {
  width: 1.35rem;
  height: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.5rem;
  border-radius: 0.35rem;
  background: #dce8f5;
  color: #34516f;
  font-size: 0.63rem;
}

.flow-inspector-tabs {
  display: flex;
  padding: 0.6rem 0.8rem 0;
  border-bottom: 1px solid #dfe7f0;
}

.flow-inspector-tabs button {
  padding: 0.52rem 0.65rem;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #62748b;
  font-size: 0.67rem;
  font-weight: 700;
}

.flow-inspector-tabs button + button {
  margin-left: 0.25rem;
}

.flow-inspector-tabs button.active {
  border-bottom-color: #2f72c7;
  color: #183e69;
}

.flow-inspector-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  background: #f8fafc;
}

.flow-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.9rem;
}

.flow-field > span {
  margin-bottom: 0.3rem;
  color: #334f6d;
  font-size: 0.67rem;
  font-weight: 700;
}

.flow-field small {
  margin-top: 0.3rem;
  color: #687a90;
  font-size: 0.62rem;
  line-height: 1.4;
}

.flow-field .input,
.flow-field .select,
.flow-field .textarea {
  width: 100%;
  font-size: 0.76rem;
}

.flow-field .textarea {
  resize: vertical;
}

.flow-field :deep(.input),
.flow-field :deep(.select),
.flow-field :deep(.textarea) {
  border-color: #d1dce9;
  border-radius: 0.45rem;
  background: #ffffff;
  color: #1e3550;
  box-shadow: none;
}

.flow-field :deep(.input:focus),
.flow-field :deep(.select:focus),
.flow-field :deep(.textarea:focus) {
  border-color: #4c82bf;
  box-shadow: 0 0 0 3px rgba(47, 114, 199, 0.1);
  outline: none;
}

.flow-check-card {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.9rem;
  padding: 0.7rem;
  border: 1px solid #d6e0eb;
  border-radius: 0.5rem;
  background: #ffffff;
}

.flow-check-card input {
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.flow-check-card > span {
  display: flex;
  flex-direction: column;
  margin-left: 0.6rem;
  color: #334f6d;
  font-size: 0.69rem;
}

.flow-check-card small {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.64rem;
  line-height: 1.35;
}

.flow-check-card-compact {
  align-items: center;
  padding: 0.55rem;
}

.flow-check-card-compact input {
  margin-top: 0;
}

.flow-friendly-card {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding: 0.75rem;
  border: 1px solid #d6e6f7;
  border-radius: 0.5rem;
  background: #eef5fc;
  color: #2b609b;
}

.flow-friendly-card svg {
  flex-shrink: 0;
}

.flow-friendly-card p {
  margin: 0 0 0 0.55rem;
  font-size: 0.7rem;
  line-height: 1.5;
}

.flow-inline-fields {
  display: flex;
  align-items: flex-start;
}

.flow-inline-fields > .flow-field {
  flex: 1;
}

.flow-inline-fields > .flow-field + .flow-field {
  margin-left: 0.55rem;
}

.flow-inline-fields > .flow-method-field {
  flex: 0 0 7rem;
}

.flow-inline-fields > .flow-port-field {
  flex: 0 0 6.5rem;
}

.flow-subsection {
  margin: 1rem 0;
  padding-top: 0.8rem;
  border-top: 1px solid #dce5ef;
}

.flow-subsection h3 {
  margin: 0 0 0.65rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.flow-code {
  font-family: Consolas, Monaco, monospace;
  font-size: 0.7rem;
  text-align: left;
  unicode-bidi: plaintext;
}

.flow-result-card {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.9rem;
  padding: 0.7rem;
  border: 1px solid #dce5ef;
  border-radius: 0.5rem;
  background: #f1f5f9;
}

.flow-result-card span {
  color: #64748b;
  font-size: 0.63rem;
  font-weight: 700;
  text-transform: uppercase;
}

.flow-result-card code {
  margin-top: 0.25rem;
  color: #334155;
  font-size: 0.68rem;
  overflow-wrap: anywhere;
}

.flow-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 0.8rem;
}

.flow-section-heading h3 {
  margin: 0;
  color: #172033;
  font-size: 0.8rem;
  font-weight: 700;
}

.flow-section-heading p {
  margin: 0.2rem 0 0;
  color: #64748b;
  font-size: 0.64rem;
}

.flow-section-heading .btn {
  margin-left: 0.6rem;
  flex-shrink: 0;
}

.flow-mini-empty {
  margin-bottom: 0.8rem;
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  color: #64748b;
  font-size: 0.7rem;
  text-align: center;
}

.flow-repeat-card {
  padding: 0.75rem;
  border: 1px solid #d8e1ed;
  border-radius: 0.5rem;
  background: #ffffff;
}

.flow-repeat-card + .flow-repeat-card {
  margin-top: 0.7rem;
}

.flow-repeat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7rem;
  font-size: 0.72rem;
}

.flow-inline-alert {
  margin-bottom: 0.75rem;
  font-size: 0.7rem;
}

.flow-inspector-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-top: 1px solid #d8e1ed;
  background: #ffffff;
}
</style>
